import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchAllUsers, User } from '../services/api';

/**
 * Reusable hook for looking up user information by ID
 * Handles ID mismatches (UUID vs username) and provides a consistent interface
 * 
 * @param userIds - Array of user IDs to look up (can be UUIDs or usernames)
 * @returns Object with:
 *   - users: Map of userId -> User
 *   - getUser: Function to get user by ID
 *   - getUserName: Function to get user name by ID (returns 'Unknown' if not found)
 *   - isLoading: Loading state
 *   - error: Error state
 */
export const useUserLookup = (userIds?: (string | null | undefined)[]) => {
  // Fetch all users
  const { data: allUsers, isLoading, error } = useQuery<User[]>({
    queryKey: ['users'],
    queryFn: fetchAllUsers,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Create a map of users by ID for fast lookup
  // Also create a map by email for username-based lookups
  const userMap = useMemo(() => {
    if (!allUsers) return new Map<string, User>();
    
    const map = new Map<string, User>();
    
    // Index by ID (UUID)
    allUsers.forEach(user => {
      if (user.id) {
        map.set(user.id, user);
      }
    });
    
    // Also index by email (for username-based lookups)
    allUsers.forEach(user => {
      if (user.email && !map.has(user.email)) {
        map.set(user.email, user);
      }
    });
    
    return map;
  }, [allUsers]);

  // Filter to only requested users if userIds provided
  const filteredUsers = useMemo(() => {
    if (!userIds || userIds.length === 0) return allUsers || [];
    if (!allUsers) return [];
    
    const userIdSet = new Set(userIds.filter(Boolean));
    return allUsers.filter(user => 
      userIdSet.has(user.id) || 
      (user.email && userIdSet.has(user.email))
    );
  }, [allUsers, userIds]);

  /**
   * Get user by ID (handles both UUID and username)
   */
  const getUser = (userId?: string | null): User | undefined => {
    if (!userId) return undefined;
    
    // Try direct ID lookup first
    const user = userMap.get(userId);
    if (user) return user;
    
    // If not found, try email lookup (for username-based IDs)
    // This handles cases where registered IDs might be usernames
    return Array.from(userMap.values()).find(u => u.email === userId || u.id === userId);
  };

  /**
   * Get user name by ID (returns 'Unknown' if not found, 'TBD' if no ID)
   */
  const getUserName = (userId?: string | null): string => {
    if (!userId) return 'TBD';
    const user = getUser(userId);
    return user?.name || 'Unknown';
  };

  /**
   * Get multiple users by IDs
   */
  const getUsers = (ids: (string | null | undefined)[]): User[] => {
    return ids
      .filter(Boolean)
      .map(id => getUser(id))
      .filter((user): user is User => user !== undefined);
  };

  return {
    users: filteredUsers,
    userMap,
    getUser,
    getUserName,
    getUsers,
    isLoading,
    error,
  };
};

